import { useMemo, useState, type FormEvent } from "react";
import { ArrowRight, CheckCircle2, Download, Gauge, Share2, Sparkles } from "lucide-react";
import { track } from "@/lib/analytics";
import {
  calculateProcurementPotential,
  type ProcurementPotentialInput,
} from "@/lib/possibility-layer";

const outcomes = [
  ["Employees move without chasing", "Requests, answers and accountable next actions stay in one governed journey.", "/product"],
  ["Managers decide from one brief", "Approval Briefs keep purpose, value, supplier evidence, controls and prior decisions together.", "/procurement-approval-software"],
  ["Procurement sees demand earlier", "Approved requirements, locations and sourcing records make upcoming demand reviewable.", "/product"],
  ["Finance sees commitments sooner", "Issued orders, receipts, invoices and payment evidence stay connected before close.", "/product"],
  ["Suppliers receive clearer requests", "Invited suppliers respond and acknowledge orders through an attributable workspace.", "/supplier-network"],
  ["Audits become an export", "Governed evidence packs and source-linked exports reduce the evidence-retrieval hunt.", "/security"],
  ["Existing assets can be considered first", "Asset custody and transfer records preserve condition, location, custodian and approval evidence.", "/product"],
  ["Recurring buying becomes governable", "Contracts, rates, reorder templates and blanket-order controls preserve the commercial basis.", "/product"],
  ["Growth keeps financial control", "Budgets, approvals, tenant boundaries and immutable evidence scale with the organisation.", "/security"],
] as const;

const pillars = ["Move faster", "Choose better", "Strengthen suppliers", "Protect working capital", "Grow with control"] as const;

export function WhatBecomesPossible() {
  return (
    <section className="bg-surface-paper px-5 py-20 text-on-paper md:px-8 lg:py-28" aria-labelledby="possibility-title">
      <div className="mx-auto max-w-7xl">
        <p className="marketing-eyebrow-light">THE POSSIBILITY LAYER</p>
        <div className="mt-4 grid gap-8 lg:grid-cols-[1fr_.7fr] lg:items-end">
          <div>
            <h2 id="possibility-title" className="max-w-5xl font-serif text-4xl tracking-[-.035em] sm:text-5xl">
              What becomes possible with RAQOZ
            </h2>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-on-paper-muted">
              RAQOZ is not only controlling procurement. It is creating procurement advantage—while every outcome remains connected to real workflow evidence.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 lg:justify-end">
            {pillars.map((pillar) => <span className="rounded-full border border-line-light bg-surface-card px-3 py-2 text-xs font-bold text-teal-deep" key={pillar}>{pillar}</span>)}
          </div>
        </div>
        <div className="mt-12 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {outcomes.map(([title, body, href]) => (
            <a className="group rounded-2xl border border-line-light bg-surface-card p-6 transition hover:border-teal-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-deep" href={href} key={title}>
              <CheckCircle2 aria-hidden="true" className="size-5 text-teal-deep" />
              <h3 className="mt-5 font-serif text-2xl">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-on-paper-muted">{body}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-teal-deep">See the capability <ArrowRight aria-hidden="true" className="size-4 transition group-hover:translate-x-1" /></span>
            </a>
          ))}
        </div>
        <a className="marketing-button-primary-light mt-10" href="/tools/procurement-potential">
          Assess your procurement potential <ArrowRight aria-hidden="true" className="size-4" />
        </a>
      </div>
    </section>
  );
}

const initial: ProcurementPotentialInput = {
  annualSpend: 0,
  recurringPercent: 0,
  emergencyPercent: 0,
  monthlyRequests: 0,
  approvalDays: 0,
  competitiveQuotePercent: 0,
  openCommitmentVisibilityPercent: 0,
  assetVisibilityPercent: 0,
  auditPreparationDays: 0,
  userAssumptionLowPercent: 0,
  userAssumptionHighPercent: 0,
};

const currency = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

export function ProcurementPotentialPage() {
  const [input, setInput] = useState(initial);
  const [complete, setComplete] = useState(false);
  const result = useMemo(() => calculateProcurementPotential(input), [input]);
  const valid = input.annualSpend > 0 && input.monthlyRequests > 0 && input.approvalDays > 0 &&
    input.userAssumptionLowPercent >= 0 && input.userAssumptionHighPercent >= input.userAssumptionLowPercent;

  function field(key: keyof ProcurementPotentialInput, value: string) {
    setInput(current => ({ ...current, [key]: Number(value) }));
    setComplete(false);
  }

  function assess(event: FormEvent) {
    event.preventDefault();
    if (!valid) return;
    setComplete(true);
    track("tool_completed", { tool: "procurement_potential", source: "browser_only_assessment" });
    requestAnimationFrame(() => document.getElementById("procurement-potential-result")?.focus());
  }

  function summary() {
    return [
      "RAQOZ Procurement Potential — indicative assessment",
      `Improvement range using your assumptions: ${currency.format(result.improvementLow)}–${currency.format(result.improvementHigh)}`,
      `Spend suitable for consolidation review: ${currency.format(result.consolidationReviewSpend)}`,
      `Spend suitable for negotiation review: ${currency.format(result.negotiationReviewSpend)}`,
      `Approval days potentially recoverable per cycle: ${result.approvalDaysRecoverableRange[0]}–${result.approvalDaysRecoverableRange[1]}`,
      `Working-capital visibility gap: ${currency.format(result.commitmentVisibilityGap)} of commitments requiring visibility review`,
      `Data quality: ${result.confidence}`,
      "",
      "Recommended next actions:",
      ...result.actions.map((item, index) => `${index + 1}. ${item}`),
      "",
      "Methodology:",
      ...result.methodology.map(item => `- ${item}`),
      "",
      "Assumptions and disclaimer:",
      ...result.assumptions.map(item => `- ${item}`),
    ].join("\n");
  }

  function download() {
    const url = URL.createObjectURL(new Blob([summary()], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "raqoz-procurement-potential-summary.txt";
    link.click();
    URL.revokeObjectURL(url);
  }

  async function share() {
    const text = summary();
    if (navigator.share) await navigator.share({ title: "RAQOZ Procurement Potential", text });
    else await navigator.clipboard.writeText(text);
  }

  return (
    <section className="bg-surface-paper px-5 py-16 text-on-paper md:px-8 lg:py-24">
      <div className="mx-auto max-w-6xl">
        <p className="marketing-eyebrow-light">FREE TOOL · NO SIGNUP</p>
        <h1 className="mt-5 max-w-5xl font-serif text-5xl leading-[1.03] tracking-[-.045em] sm:text-6xl">Procurement Potential</h1>
        <p className="mt-6 max-w-3xl text-lg leading-8 text-on-paper-muted">
          Estimate where better visibility and review may help. Inputs stay in this browser unless you explicitly download or share the result.
        </p>
        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_.9fr]">
          <form className="rounded-[2rem] border border-line-light bg-surface-card p-6 sm:p-8" onSubmit={assess}>
            <h2 className="font-serif text-3xl">Describe your current process</h2>
            <p className="mt-3 text-sm leading-6 text-on-paper-muted">Use directional figures. Do not enter supplier names, confidential records or personal information.</p>
            <div className="mt-7 grid gap-5 sm:grid-cols-2">
              <NumberField label="Annual addressable purchase spend (₹)" value={input.annualSpend} onChange={value => field("annualSpend", value)} min={1} />
              <NumberField label="Monthly purchase requests" value={input.monthlyRequests} onChange={value => field("monthlyRequests", value)} min={1} />
              <NumberField label="Typical approval duration (days)" value={input.approvalDays} onChange={value => field("approvalDays", value)} min={1} />
              <NumberField label="Recurring purchases (%)" value={input.recurringPercent} onChange={value => field("recurringPercent", value)} />
              <NumberField label="Emergency purchases (%)" value={input.emergencyPercent} onChange={value => field("emergencyPercent", value)} />
              <NumberField label="Purchases with competitive quotes (%)" value={input.competitiveQuotePercent} onChange={value => field("competitiveQuotePercent", value)} />
              <NumberField label="Open commitments visible (%)" value={input.openCommitmentVisibilityPercent} onChange={value => field("openCommitmentVisibilityPercent", value)} />
              <NumberField label="Assets visible and transferable (%)" value={input.assetVisibilityPercent} onChange={value => field("assetVisibilityPercent", value)} />
              <NumberField label="Audit evidence preparation (days)" value={input.auditPreparationDays} onChange={value => field("auditPreparationDays", value)} />
            </div>
            <fieldset className="mt-7 rounded-2xl border border-line-light p-5">
              <legend className="px-2 text-sm font-bold">Your conservative improvement assumption</legend>
              <p className="mb-4 text-xs leading-5 text-on-paper-muted">RAQOZ supplies no industry benchmark here. Enter your own low and high scenario percentages.</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <NumberField label="Low scenario (%)" value={input.userAssumptionLowPercent} onChange={value => field("userAssumptionLowPercent", value)} />
                <NumberField label="High scenario (%)" value={input.userAssumptionHighPercent} onChange={value => field("userAssumptionHighPercent", value)} />
              </div>
            </fieldset>
            <button className="marketing-button-primary-light mt-7" disabled={!valid} type="submit">Calculate indicative range <Gauge aria-hidden="true" className="size-4" /></button>
          </form>
          <aside className="h-fit rounded-[2rem] bg-surface-ink p-6 text-on-ink sm:p-8" aria-live="polite">
            {!complete ? (
              <div className="py-8">
                <Sparkles aria-hidden="true" className="size-8 text-teal-bright" />
                <h2 className="mt-5 font-serif text-3xl text-on-ink-strong">Your basic result is immediate.</h2>
                <p className="mt-4 leading-7 text-on-ink">No account, email or lead form is required. The result separates review potential from realised or finance-verified value.</p>
              </div>
            ) : (
              <div id="procurement-potential-result" tabIndex={-1}>
                <p className="marketing-eyebrow-dark">INDICATIVE RANGE · {result.confidence.toUpperCase()} DATA QUALITY</p>
                <h2 className="mt-4 font-serif text-4xl text-on-ink-strong">{currency.format(result.improvementLow)}–{currency.format(result.improvementHigh)}</h2>
                <p className="mt-3 text-sm leading-6 text-on-ink">Possible annual improvement under your own assumptions. This is potential—not realised savings or a guarantee.</p>
                <dl className="mt-7 grid gap-4">
                  <Result label="Consolidation review spend" value={currency.format(result.consolidationReviewSpend)} />
                  <Result label="Negotiation review spend" value={currency.format(result.negotiationReviewSpend)} />
                  <Result label="Approval time scenario" value={`${result.approvalDaysRecoverableRange[0]}–${result.approvalDaysRecoverableRange[1]} days per cycle`} />
                  <Result label="Commitment visibility gap" value={currency.format(result.commitmentVisibilityGap)} />
                  <Result label="Audit-readiness opportunity" value={result.auditReadinessOpportunity} />
                </dl>
                <div className="mt-7 rounded-xl border border-line-dark p-4 text-xs leading-5 text-on-ink">
                  <strong className="text-on-ink-strong">Methodology</strong>
                  <ul className="mt-2 grid gap-2">{result.methodology.map(item => <li key={item}>{item}</li>)}</ul>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button className="marketing-button-primary-dark" onClick={download} type="button"><Download aria-hidden="true" className="size-4" /> Download summary</button>
                  <button className="marketing-button-secondary-dark" onClick={share} type="button"><Share2 aria-hidden="true" className="size-4" /> Share result</button>
                </div>
                <a className="marketing-text-link-dark mt-6" href="/contact">Request an optional walkthrough <ArrowRight aria-hidden="true" className="size-4" /></a>
              </div>
            )}
          </aside>
        </div>
      </div>
    </section>
  );
}

function NumberField({ label, value, onChange, min = 0 }: { label: string; value: number; onChange: (value: string) => void; min?: number }) {
  return <label className="grid gap-2 text-sm font-semibold">{label}<input className="min-h-11 rounded-xl border border-line-light bg-white px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-deep" min={min} max={1000000000000} step="any" inputMode="decimal" type="number" value={value || ""} onChange={event => onChange(event.target.value)} required /></label>;
}

function Result({ label, value }: { label: string; value: string }) {
  return <div className="border-b border-line-dark pb-3"><dt className="text-xs text-on-ink-muted">{label}</dt><dd className="mt-1 font-semibold text-on-ink-strong">{value}</dd></div>;
}
